import { LocalDate } from '@js-joda/core';
import { useEffect, useState } from 'react';
import { EnergyType, Property } from '../api/properties/types';
import { useCurrentWeekEnergyBudget, useWeeklyEnergyBudgets } from '../api/weekly-energy-budgets';

interface UseWeeklySummaryStatusProps {
  propertyId?: string;
  property?: Property;
}

export const useWeeklySummaryStatus = ({ propertyId, property }: UseWeeklySummaryStatusProps = {}) => {
  const [previousBudgetStartDate, setPreviousBudgetStartDate] = useState<LocalDate>();

  const hasConsumptionDataSource = property?.sundialSite?.dataSources?.some(
    (source) => source.sourceType === EnergyType.consumption,
  );

  const isWeeklyConsumptionBudgetEligible = Boolean(
    hasConsumptionDataSource && property?.sundialSite?.earliestReadingTimestamp,
  );

  const { data: currentBudget, isLoading: isLoadingCurrentBudget } = useCurrentWeekEnergyBudget(
    propertyId || '',
    Boolean(isWeeklyConsumptionBudgetEligible),
  );

  const {
    data: previousBudget,
    isLoading: isLoadingLastBudget,
    error: previousBudgetError,
    errorStatusCode: previousBudgetErrorStatusCode,
  } = useWeeklyEnergyBudgets(
    propertyId || '',
    { startDate: previousBudgetStartDate?.toString() },
    Boolean(isWeeklyConsumptionBudgetEligible && previousBudgetStartDate),
  );

  useEffect(() => {
    if (!currentBudget) {
      return;
    }

    if (currentBudget.isCurrentWeekBudget) {
      const today = LocalDate.now();
      const daysFromMonday = (today.dayOfWeek().value() - 1) % 7;
      const monday = today.minusDays(daysFromMonday);
      const twoMondaysAgo = monday.minusDays(7);

      setPreviousBudgetStartDate(twoMondaysAgo);
    } else {
      const normalizedDate = currentBudget.startDate.replace('Z', '');
      const currentBudgetStartDate = LocalDate.parse(normalizedDate.split('T')[0]);
      const previousBudgetStartDate = currentBudgetStartDate.minusDays(7);

      setPreviousBudgetStartDate(previousBudgetStartDate);
    }
  }, [currentBudget]);

  const hasUnviewedWeeklySummary = Boolean(
    previousBudget && previousBudget.processed && !previousBudget.resultViewed,
  );

  const isCurrentBudgetQueryEnabled = Boolean(isWeeklyConsumptionBudgetEligible && propertyId);
  const isPreviousBudgetQueryEnabled = Boolean(isWeeklyConsumptionBudgetEligible && previousBudgetStartDate && propertyId);

  const isLoading =
    (isCurrentBudgetQueryEnabled && isLoadingCurrentBudget) ||
    (isPreviousBudgetQueryEnabled && isLoadingLastBudget);

  return {
    hasUnviewedWeeklySummary,
    isLoading,
    previousBudget,
    currentBudget,
    isWeeklyConsumptionBudgetEligible,
    previousBudgetError,
    previousBudgetErrorStatusCode,
  };
};
